import { useState } from 'preact/hooks';
import { api, mensagemDoErro } from '../api';
import { ROTULO_STATUS } from '../format';
import { useApi } from '../hooks';
import { useMunicipios } from '../lookups';
import type { Acao, Projeto, StatusAcao } from '../types';
import { Campo, ErroForm, Modal, avisar } from '../ui';
import { STATUS_OPCOES } from './comum';
import { CampoData } from '../componentes/Campos';

type Props = { onFechar: () => void; onSalvo: (a: Acao) => void };

export function AcaoFormModal({ onFechar, onSalvo }: Props) {
  const projetos = useApi<Projeto[]>('/projetos?ativo=true');
  const { lista: municipios } = useMunicipios();

  const [projetoId, setProjetoId] = useState('');
  const [codIbge, setCodIbge] = useState('');
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [status, setStatus] = useState<StatusAcao>('PLANEJADA');
  const [progresso, setProgresso] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const lista = projetos.dados ?? [];

  async function salvar(e: Event) {
    e.preventDefault();
    setErro('');
    if (titulo.trim().length < 3) return setErro('O título precisa ter ao menos 3 caracteres.');
    if (inicio && fim && fim < inicio) return setErro('O prazo final não pode ser anterior ao início.');
    setSalvando(true);
    try {
      const a = await api<Acao>('/acoes', {
        method: 'POST',
        body: {
          projetoId,
          codIbge,
          titulo: titulo.trim(),
          descricao: descricao.trim() || null,
          responsavel: responsavel.trim() || null,
          dataInicio: inicio || null,
          dataFimPrevista: fim || null,
          status,
          ...(progresso !== '' ? { progressoPct: Number(progresso) } : {}),
        },
      });
      avisar('Ação cadastrada.');
      onSalvo(a);
    } catch (err) {
      setErro(mensagemDoErro(err));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal titulo="Nova ação" onFechar={onFechar}>
      <form class="form" onSubmit={salvar}>
        {lista.length === 0 && !projetos.carregando && (
          <p class="aviso">Não há projeto ativo disponível. Cadastre um projeto antes de criar ações.</p>
        )}
        <Campo rotulo="Projeto *">
          {(id) => (
            <select id={id} required value={projetoId} onChange={(e) => setProjetoId(e.currentTarget.value)}>
              <option value="">Selecione…</option>
              {lista.map((p) => <option key={p.id} value={p.id}>{p.titulo}</option>)}
            </select>
          )}
        </Campo>
        <Campo rotulo="Município *">
          {(id) => (
            <select id={id} required value={codIbge} onChange={(e) => setCodIbge(e.currentTarget.value)}>
              <option value="">Selecione…</option>
              {municipios.map((m) => <option key={m.cod} value={m.cod}>{m.nome}</option>)}
            </select>
          )}
        </Campo>
        <Campo rotulo="Título *" dica="Mínimo de 3 caracteres.">
          {(id) => <input id={id} required minLength={3} maxLength={200} value={titulo} onInput={(e) => setTitulo(e.currentTarget.value)} />}
        </Campo>
        <Campo rotulo="Descrição">
          {(id) => <textarea id={id} rows={3} maxLength={5000} value={descricao} onInput={(e) => setDescricao(e.currentTarget.value)} />}
        </Campo>
        <div class="linha-form">
          <Campo rotulo="Início" larg="meio">{(id) => <CampoData id={id} value={inicio} onChange={setInicio} />}</Campo>
          <Campo rotulo="Prazo final" larg="meio">{(id) => <CampoData id={id} value={fim} onChange={setFim} />}</Campo>
        </div>
        <Campo rotulo="Responsável">
          {(id) => <input id={id} maxLength={150} value={responsavel} onInput={(e) => setResponsavel(e.currentTarget.value)} />}
        </Campo>
        <div class="linha-form">
          <Campo rotulo="Situação inicial" larg="meio">
            {(id) => (
              <select id={id} value={status} onChange={(e) => setStatus(e.currentTarget.value as StatusAcao)}>
                {STATUS_OPCOES.map((s) => <option key={s} value={s}>{ROTULO_STATUS[s]}</option>)}
              </select>
            )}
          </Campo>
          <Campo rotulo="Progresso inicial (%)" larg="meio">
            {(id) => <input id={id} type="number" min={0} max={100} step={1} value={progresso} onInput={(e) => setProgresso(e.currentTarget.value)} placeholder="0" />}
          </Campo>
        </div>
        <ErroForm texto={erro} />
        <div class="modal-acoes">
          <button type="button" class="botao" onClick={onFechar}>Cancelar</button>
          <button type="submit" class="botao primario" disabled={salvando}>{salvando ? 'Salvando…' : 'Cadastrar ação'}</button>
        </div>
      </form>
    </Modal>
  );
}
